import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase"; // Changed from @/integrations/supabase/client
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useProviderCategories } from "@/hooks/useProviders";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GlassCard from "@/components/GlassCard";
import { toast } from "sonner"; // Changed from use-toast to sonner as App.jsx uses Sonner
import {
    ArrowLeft,
    BadgeCheck,
    Star,
    Loader2,
    Edit2,
    Trash2,
    Check,
    Search,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { SuburbSelect } from "@/components/SuburbSelect";

const AdminPortal = () => {
    const navigate = useNavigate();
    const { isAdmin, loading: authLoading } = useAuth();
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");
    const [searchQuery, setSearchQuery] = useState("");
    const [editingProvider, setEditingProvider] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const { categories } = useProviderCategories();

    useEffect(() => {
        if (!authLoading && !isAdmin) {
            navigate("/");
            toast.error("Access Denied: You don't have permission to access this page.");
        }
    }, [isAdmin, authLoading, navigate]);

    const fetchProviders = useCallback(async () => {
        setLoading(true);
        let query = supabase.from("providers").select("*").order("created_at", { ascending: false });

        if (filter === "pending") {
            query = query.eq("is_verified", false).neq("is_rejected", true);
        } else if (filter === "verified") {
            query = query.eq("is_verified", true);
        }

        const { data, error } = await query;

        if (error) {
            toast.error("Failed to fetch providers");
        } else {
            setProviders(data || []);
        }
        setLoading(false);
    }, [filter]);

    useEffect(() => {
        if (isAdmin) {
            fetchProviders();
        }
    }, [isAdmin, fetchProviders]);

    const filteredProviders = useMemo(() => {
        return providers.filter((provider) => {
            const nameMatch = !searchQuery || provider.name?.toLowerCase().includes(searchQuery.toLowerCase());
            const categoryMatch = !searchQuery || provider.category?.toLowerCase().includes(searchQuery.toLowerCase());
            return nameMatch || categoryMatch;
        });
    }, [providers, searchQuery]);

    const groupedProviders = useMemo(() => {
        if (filter !== "verified" && filter !== "all") return null;

        const groups = {};
        filteredProviders.forEach((provider) => {
            const cat = provider.category || "Uncategorized";
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(provider);
        });

        // Sort categories alphabetically
        return Object.keys(groups).sort().reduce((acc, key) => {
            acc[key] = groups[key];
            return acc;
        }, {});
    }, [filteredProviders, filter]);

    const renderProviderCard = (provider) => (
        <GlassCard key={provider.id} className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">
                            {provider.name}
                        </h3>
                        {provider.is_verified && (
                            <BadgeCheck className="w-4 h-4 text-primary" />
                        )}
                        {provider.is_featured && (
                            <span className="text-xs bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded-full">
                                Featured
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        {provider.category}
                    </p>
                    {provider.phone && (
                        <p className="text-sm text-muted-foreground">
                            📞 {provider.phone}
                        </p>
                    )}
                    {provider.service_area && (
                        <p className="text-sm text-muted-foreground">
                            📍 {provider.service_area}
                        </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                        Added: {new Date(provider.created_at).toLocaleDateString()}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    {!provider.is_verified && (
                        <Button
                            size="sm"
                            onClick={() => handleVerify(provider)}
                            className="rounded-xl"
                        >
                            <Check className="w-4 h-4 mr-1" />
                            Verify
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant={provider.is_featured ? "secondary" : "outline"}
                        onClick={() => handleToggleFeatured(provider)}
                        className="rounded-xl"
                    >
                        <Star
                            className={`w-4 h-4 mr-1 ${provider.is_featured ? "fill-current" : ""
                                }`}
                        />
                        {provider.is_featured ? "Unfeature" : "Feature"}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setEditingProvider({
                                ...provider,
                                service_suburbs_text: provider.service_suburbs?.join(", ") || ""
                            });
                            setIsEditDialogOpen(true);
                        }}
                        className="rounded-xl"
                    >
                        <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => !provider.is_verified ? handleReject(provider.id) : handleDelete(provider.id)}
                        className="rounded-xl text-destructive hover:text-destructive gap-1"
                    >
                        <Trash2 className="w-4 h-4" />
                        {!provider.is_verified ? "Reject" : "Delete"}
                    </Button>
                </div>
            </div>
        </GlassCard>
    );

    const handleVerify = async (provider) => {
        const categoryId = provider.category_id || getCategoryIdFromName(provider.category);

        const { error } = await supabase
            .from("providers")
            .update({
                is_verified: true,
                category_id: categoryId
            })
            .eq("id", provider.id);

        if (error) {
            toast.error("Failed to verify provider");
        } else {
            toast.success("Provider verified and added to public directory");
            fetchProviders();
        }
    };

    const handleToggleFeatured = async (provider) => {
        const { error } = await supabase
            .from("providers")
            .update({ is_featured: !provider.is_featured })
            .eq("id", provider.id);

        if (error) {
            toast.error("Failed to update featured status");
        } else {
            toast.success(provider.is_featured
                ? "Provider removed from featured list"
                : "Provider added to featured specialists");
            fetchProviders();
        }
    };

    const handleReject = async (providerId) => {
        const { error } = await supabase
            .from("providers")
            .update({ is_rejected: true })
            .eq("id", providerId);

        if (error) {
            toast.error("Failed to reject provider");
        } else {
            toast.success("Provider rejected — hidden from public directory but still visible to the submitter.");
            fetchProviders();
        }
    };

    const handleDelete = async (providerId) => {
        const { error } = await supabase
            .from("providers")
            .delete()
            .eq("id", providerId);

        if (error) {
            toast.error("Failed to delete provider");
        } else {
            toast.success("Provider deleted permanently");
            fetchProviders();
        }
    };

    const handleSaveEdit = async () => {
        if (!editingProvider) return;

        const suburbsArray = editingProvider.service_suburbs_text
            ? editingProvider.service_suburbs_text.split(",").map(s => s.trim()).filter(Boolean)
            : (Array.isArray(editingProvider.service_suburbs) ? editingProvider.service_suburbs : []);

        const { error } = await supabase
            .from("providers")
            .update({
                name: editingProvider.name,
                category: editingProvider.category,
                category_id: getCategoryIdFromName(editingProvider.category),
                phone: editingProvider.phone,
                email: editingProvider.email,
                service_area: editingProvider.service_area,
                service_suburbs: suburbsArray
            })
            .eq("id", editingProvider.id);

        if (error) {
            toast.error("Failed to update provider");
        } else {
            toast.success("Provider details updated");
            setIsEditDialogOpen(false);
            setEditingProvider(null);
            fetchProviders();
        }
    };

    const getCategoryIdFromName = (name) => {
        const mapping = {
            "Plumbing": "plumbing",
            "Electrical": "electrical",
            "Painting": "painting",
            "Garden & Pool": "garden-pool",
            "Security": "security",
            "Roofing": "roofing",
            "HVAC": "hvac",
            "Gas": "gas"
        };
        // If the category is not in our mapping (e.g. from DB), 
        // find the actual category object to get its UUID
        const categoryObj = categories.find(c => c.name === name);
        if (categoryObj) return categoryObj.id;

        return mapping[name] || name.toLowerCase().replace(/\s+/g, "-");
    };

    if (authLoading || !isAdmin) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-8">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-40 bg-primary/95 backdrop-blur-sm" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate("/profile")}
                        className="text-primary-foreground hover:bg-primary-foreground/10"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-base font-medium text-primary-foreground">
                        Admin Portal
                    </h1>
                </div>
            </header>

            {/* Main Content */}
            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 5rem)" }}>
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Filter Tabs and Search */}
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="flex gap-2 w-full sm:w-auto">
                            {(["pending", "verified", "all"]).map((status) => (
                                <Button
                                    key={status}
                                    variant={filter === status ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => {
                                        setFilter(status);
                                        setSearchQuery(""); // Reset search on filter change
                                    }}
                                    className="capitalize rounded-xl flex-1 sm:flex-initial"
                                >
                                    {status}
                                    {status === "pending" && providers.length > 0 && filter !== "pending" && (
                                        <span className="ml-2 bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full">
                                            !
                                        </span>
                                    )}
                                </Button>
                            ))}
                        </div>

                        <div className="relative w-full sm:max-w-xs">
                            <Input
                                placeholder="Search by name or category..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pr-10 rounded-2xl h-10 bg-white/50 border-none shadow-sm focus-visible:ring-primary/20"
                            />
                            <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        </div>
                    </div>

                    {/* Providers List */}
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        </div>
                    ) : filteredProviders.length === 0 ? (
                        <GlassCard className="p-8 text-center">
                            <p className="text-muted-foreground">
                                {searchQuery ? "No matching providers found" : `No ${filter === "all" ? "" : filter} providers found`}
                            </p>
                        </GlassCard>
                    ) : groupedProviders ? (
                        /* Grouped list by Category (for Verified or All) */
                        <div className="space-y-8">
                            {Object.entries(groupedProviders).map(([categoryName, categoryProviders]) => (
                                <div key={categoryName} className="space-y-3">
                                    <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1 flex items-center justify-between">
                                        <span>{categoryName}</span>
                                        <span className="bg-primary/10 text-primary px-2.5 py-0.5 rounded-full text-[10px] font-extrabold">
                                            {categoryProviders.length}
                                        </span>
                                    </h3>
                                    <div className="space-y-4">
                                        {categoryProviders.map((provider) => renderProviderCard(provider))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Simple list (for Pending) */
                        <div className="space-y-4">
                            {filteredProviders.map((provider) => renderProviderCard(provider))}
                        </div>
                    )}
                </div>
            </main>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Provider</DialogTitle>
                    </DialogHeader>

                    {editingProvider && (
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-name">Name</Label>
                                <Input
                                    id="edit-name"
                                    value={editingProvider.name}
                                    onChange={(e) =>
                                        setEditingProvider({
                                            ...editingProvider,
                                            name: e.target.value,
                                        })
                                    }
                                    className="rounded-xl"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-category">Category</Label>
                                <Select
                                    value={editingProvider.category}
                                    onValueChange={(value) =>
                                        setEditingProvider({
                                            ...editingProvider,
                                            category: value,
                                        })
                                    }
                                >
                                    <SelectTrigger className="rounded-xl">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.name}>
                                                {cat.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-phone">Phone</Label>
                                <Input
                                    id="edit-phone"
                                    value={editingProvider.phone || ""}
                                    onChange={(e) =>
                                        setEditingProvider({
                                            ...editingProvider,
                                            phone: e.target.value,
                                        })
                                    }
                                    className="rounded-xl"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-email">Email</Label>
                                <Input
                                    id="edit-email"
                                    type="email"
                                    value={editingProvider.email || ""}
                                    onChange={(e) =>
                                        setEditingProvider({
                                            ...editingProvider,
                                            email: e.target.value,
                                        })
                                    }
                                    className="rounded-xl"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-area">Public Display Area (e.g. "Serving Northern Suburbs")</Label>
                                <Input
                                    id="edit-area"
                                    value={editingProvider.service_area || ""}
                                    onChange={(e) =>
                                        setEditingProvider({
                                            ...editingProvider,
                                            service_area: e.target.value,
                                        })
                                    }
                                    placeholder="This text appears on the provider card"
                                    className="rounded-xl"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-areas">Search Index Suburbs (Comma separated)</Label>
                                <Input
                                    id="edit-areas"
                                    value={editingProvider.service_suburbs_text || ""}
                                    onChange={(e) =>
                                        setEditingProvider({
                                            ...editingProvider,
                                            service_suburbs_text: e.target.value,
                                        })
                                    }
                                    placeholder="Sunningdale, Table View, Blouberg..."
                                    className="rounded-xl"
                                />
                                <p className="text-[10px] text-muted-foreground ml-1 italic">
                                    Admins: Add as many specific suburbs here as needed for search optimization.
                                </p>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsEditDialogOpen(false)}
                                    className="flex-1 rounded-xl"
                                >
                                    Cancel
                                </Button>
                                <Button onClick={handleSaveEdit} className="flex-1 rounded-xl">
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminPortal;
